import { MobXProviderContext } from 'mobx-react';
import type { IMessageReferences } from 'Models/MessageModel';
import * as React from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { extractLastLink, normalizeUrl } from 'Utils/linkUtils';
import { compileEditorMarkdown } from './compileEditorMarkdown';
import { compileViewerMarkdown } from './compileViewerMarkdown';

export type useMarkdownCompilerProps = (
  messageId: string,
  references?: IMessageReferences[]
) => {
  compileViewerMarkdown: (content: string) => React.ReactNode;
  compileEditorMarkdown: (content: string) => string;
};

export const useMarkdownCompiler: useMarkdownCompilerProps = (
  messageId,
  references = []
) => {
  const {
    message: { loadLinkPreview },
    participant: { selectParticipantPersons },
    conversation: { CurrentConversation },
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  const participantPersons = selectParticipantPersons(
    CurrentConversation?.id || ''
  );

  const isGroupChat =
    CurrentConversation?.grouping === 'Channel' ||
    CurrentConversation?.grouping === 'Group';

  const isSMSConversation = CurrentConversation?.IsSmsConversation ?? false;

  const includeMentions = isGroupChat && !isSMSConversation;

  return {
    compileViewerMarkdown: (content: string) => {
      // Extract the last link from the content
      const lastLink = extractLastLink(content);

      // Create a buildLinkPreview function that only loads preview for the last link
      const buildLinkPreview = (url: string) => {
        if (!messageId || !lastLink) return;

        // Only load preview if this is the last link in the message
        if (normalizeUrl(url) === normalizeUrl(lastLink)) {
          void loadLinkPreview(url, CurrentConversation?.id || '', messageId);
        }
      };

      return compileViewerMarkdown({
        content,
        includeMentions,
        participantPersons,
        buildLinkPreview,
        references,
      });
    },
    compileEditorMarkdown: (content: string) =>
      compileEditorMarkdown({ content, participantPersons, includeMentions }),
  };
};
